import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, of, zip } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';

import { ApiStoresService } from 'idt-ecommerce-api/_api/api-catalog/api-stores/api-stores.service';
import { ApiNavigationStorefrontService } from 'idt-ecommerce-api/_api/api-navigation-storefront/api-navigation-storefront.service';
import { Category } from 'idt-ecommerce-api/catalog/categories/categories.models';
import { StorefrontMenuTransformer } from 'idt-ecommerce-api/catalog/categories/storefront-menu.transformer';
import { EcommerceConfig } from 'idt-ecommerce-api/config/config.models';
import { EcommerceConfigService } from 'idt-ecommerce-api/config/ecommerce-config.service';
import { Seo } from 'idt-ecommerce-api/seo/seo.models';
import { SeoService } from 'idt-ecommerce-api/seo/seo.service';
import { buildStorefrontLocale } from 'idt-ecommerce-api/shared/helpers/storefront-language.helper';
import { StoreCatalog, StoreDetail, StoreLanguage, StoreSummary } from 'idt-ecommerce-api/stores/stores.models';
import { StoresModelsTransformer } from 'idt-ecommerce-api/stores/stores.models.transformer';

import { EcommerceContext } from './context.models';


@Injectable({ providedIn: 'root' })
export class ContextService {

  private readonly context$: BehaviorSubject<EcommerceContext>;

  constructor(
    private readonly apiNavigationStorefrontService: ApiNavigationStorefrontService,
    private readonly apiStoresService: ApiStoresService,
    private readonly ecommerceConfigService: EcommerceConfigService,
    private readonly seoService: SeoService
  ) {
    this.context$ = new BehaviorSubject<EcommerceContext>({
      allStores: [],
      categories: [],
      config: {} as EcommerceConfig,
      currentCatalogId: '',
      currentLanguage: {} as StoreLanguage, // TODO: replace currentLanguage by currentLanguageId
      seo: {} as Seo,
      store: {} as StoreDetail
    });
  }

  public getContext$(): Observable<EcommerceContext> {
    return this.context$;
  }

  public getAllStores$(): Observable<StoreSummary[]> {
    return this.context$.pipe(map((context) => context.allStores));
  }

  public getCategories$(): Observable<Category[]> {
    return this.context$.pipe(map((context) => context.categories));
  }

  public getCurrentLanguage$(): Observable<StoreLanguage> {
    return this.context$.pipe(map((context) => context.currentLanguage));
  }

  public getStore$(): Observable<StoreDetail> {
    return this.context$.pipe(map((context) => context.store));
  }

  public setAllStores(allStores: StoreSummary[]): void {
    this.context$.next({
      ...this.context$.getValue(),
      allStores
    });
  }

  public setStoreTimestamp(timestampBuildVersion: string): void {
    const store = this.context$.getValue().store;
    this.context$.next({
      ...this.context$.getValue(),
      store: {
        ...store,
        details: {
          ...store.details,
          timestampBuildVersion
        }
      }
    });
  }

  public init(countryCode: string, languageCode: string): void {
    if (this.isContextInitialized(countryCode, languageCode)) {
      return;
    }
    this.apiStoresService.retrieveDefaultStoreIdAndLanguageId(countryCode, languageCode).pipe(
      take(1),
      filter((result): result is { storeId: string; languageId: string } => (
        result.storeId !== undefined
      )),
      switchMap((result) => this.createContext$(result)),
      catchError((error) => {
        console.warn('ContextService.init: context initialization failed', error);
        return EMPTY;
      })
    ).subscribe((context) => {
      this.context$.next(context);
    });
  }

  public isContextInitialized(countryCode: string, languageCode: string): boolean {
    const current = this.context$.getValue();
    return current.store?.countryCode === countryCode
      && current.currentLanguage?.code === languageCode;
  }

  public initWithStoreAndLanguage(params: { storeId: string; languageId: string }): void {
    this.createContext$(params).subscribe((context) => {
      this.context$.next(context);
    });
  }

  private createContext$(params: { storeId: string; languageId: string }): Observable<EcommerceContext> {
    const config = this.ecommerceConfigService.getConfig();
    return zip(
      this.apiStoresService.storeDetail(params.storeId, params.languageId),
      this.seoService.seo()
    ).pipe(
      map(([store, seo]) => ({
        seo,
        store: StoresModelsTransformer.storeDetailFromApi(store, config.appId)
      })),
      switchMap(({ seo, store }) => {
        const currentLanguage = store.supportedLanguages.find((language) => language.id === params.languageId)!;
        // Initialize the context with actual info needed to launch the app
        const context = {
          ...this.context$.getValue(),
          config,
          currentCatalogId: store.catalogs.find(
            (catalog: StoreCatalog) => catalog.type === config.catalogType
          )!.id,
          currentLanguage,
          seo,
          store
        };
        return this.getCategories(context).pipe(
          map((categories) => ({
            ...context,
            categories
          }))
        );
      }),
      take(1)
    );
  }

  private getCategories(context: EcommerceContext): Observable<Category[]> {
    const locale = buildStorefrontLocale(context.currentLanguage, context.store.countryCode);
    const catalogId = Number(context.currentCatalogId) || undefined;

    return this.apiNavigationStorefrontService.getMenu(
      context.store.id,
      { language: locale, catalogId }
    ).pipe(
      map((menu) => StorefrontMenuTransformer.categoriesFromStorefrontMenu(menu, context)),
      catchError((error) => {
        console.error('Error retrieving categories:', error);
        return of([]);
      })
    );
  }

}
